import FloatingContainer from '@/components/base/floating/floating-container';
import useCommonFloating from '@/components/base/floating/use-common-floating';
import { popularDomains } from '@/components/base/popular-domains-prompting/popular-domains';
import { useSiteId } from '@/contexts/domain/hooks';
import { useClickOutside } from '@react-hookz/web';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

type Props = {
  value: string;
  onChange: (value: string) => void;
};

export const PopularDomainsPrompting = ({ value = '', onChange }: Props) => {
  const siteId = useSiteId();
  const domainsPerSiteId =
    popularDomains[siteId as keyof typeof popularDomains];
  const [filteredDomains, setFilteredDomains] =
    useState<string[]>(domainsPerSiteId);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (!value) {
      setShowPrompt(false);

      return;
    }

    const typedDomain = value.split('@')[1]?.toLowerCase();
    const newFilteredDomains = domainsPerSiteId.filter((domain) =>
      domain.includes(typedDomain || ''),
    );

    setFilteredDomains(newFilteredDomains);
    setShowPrompt(
      newFilteredDomains.length > 0 &&
        value.length > 2 &&
        value.includes('@') &&
        !(
          newFilteredDomains.length === 1 &&
          newFilteredDomains[0] === typedDomain
        ),
    );
  }, [value]);

  const handleClick = (domain: string) => () => {
    onChange(`${value.split('@')[0]}@${domain}`);
    setShowPrompt(false);
  };
  const clickOutsideRef = useRef<HTMLDivElement | null>(null);

  //TODO: Remove the RefObject<HTMLDivElement> when useClickOutside will be ready for react 19(not ref can't be null)
  useClickOutside<HTMLDivElement>(
    clickOutsideRef as RefObject<HTMLDivElement>,
    () => setShowPrompt(false),
  );
  const { refs, transitionRef, context, mounted, ...containerProps } =
    useCommonFloating({
      placement: 'bottom-start',
      offset: 25,
      isOpen: showPrompt && value.length > 2 && value.includes('@'),
    });

  return (
    <>
      {showPrompt && mounted && (
        <FloatingContainer
          ref={mergeRefs([refs.setFloating, clickOutsideRef])}
          transitionRef={transitionRef}
          variant="light"
          context={context}
          className="!gap-0 max-h-[226px] w-full min-w-full max-w-full overflow-y-auto"
          {...containerProps}
          arrowClassName="!-translate-x-1/2 !left-1/2 !rotate-180 !-top-[10px]"
          floatingClassName="w-full !translate-y-[52px]"
        >
          {filteredDomains.map((domain) => (
            <span
              key={domain}
              className="cursor-pointer p-xs"
              onClick={handleClick(domain)}
            >
              {value.split('@')[0]}@{domain}
            </span>
          ))}
        </FloatingContainer>
      )}
    </>
  );
};
